<script context="module">
  import gql from "graphql-tag";

  const FRAGMENTS = {};
  FRAGMENTS.HostedForm_contactField = gql`
    fragment HostedForm_contactField on ContactField {
      _id
      name
      isFirstName
      isLastName
      isEmail
      isSms
    }
  `;
  FRAGMENTS.HostedForm_formField = gql`
    fragment HostedForm_formField on FormField {
      _id
      apiName
      contactField {
        ...HostedForm_contactField
      }
    }
    ${FRAGMENTS.HostedForm_contactField}
  `;
  FRAGMENTS.HostedForm_formSpec = gql`
    fragment HostedForm_formSpec on FormSpec {
      _id
      fields {
        ...HostedForm_formField
      }
    }
    ${FRAGMENTS.HostedForm_formField}
  `;
  FRAGMENTS.HostedForm_form = gql`
    fragment HostedForm_form on PublicForm {
      _id
      name
      isDeleted
      willSendMessage
      spec {
        ...HostedForm_formSpec
      }
    }
    ${FRAGMENTS.HostedForm_formSpec}
  `;
  FRAGMENTS.HostedForm_org = gql`
    fragment HostedForm_org on PublicOrg {
      _id
      name
      logoHandle
    }
  `;

  const QUERIES = {};
  QUERIES.form = gql`
    query form($formId: ID!) {
      hostedForm(formId: $formId) {
        form {
          ...HostedForm_form
        }
        org {
          ...HostedForm_org
        }
        contactEmail
      }
    }
    ${FRAGMENTS.HostedForm_form}
    ${FRAGMENTS.HostedForm_org}
  `;

  const MUTATIONS = {};

  export { FRAGMENTS, QUERIES, MUTATIONS };
</script>

<script>
  import _get from "lodash/get";
  import _find from "lodash/find";
  import { fly, fade } from "svelte/transition";
  import { quintOut } from "svelte/easing";
  import { getClient } from "svelte-apollo";
  import party from "party-js";
  import { navigate } from "svelte-routing";
  import { Tooltip } from "@svelte-plugins/tooltips";
  import { getFilestackUrl } from "../../filestack";
  import { watchQuery } from "../../svelte-apollo-watch-query";
  import { getFormEndpoint } from "../../formEndpoints";
  import HtmlHeadTitle from "../../components/HtmlHeadTitle.svelte";
  import PrivacyAndTermsPopUp from "../../components/PrivacyAndTermsPopUp.svelte";

  export let location;
  export let formSlug;

  $: formId = atob(formSlug);

  const client = getClient();

  let isSecure = true; //window.location.protocol == 'https:';
  let formOutro = false;

  let firstNameEntry = "";
  let lastNameEntry = "";
  let emailEntry = "";
  let smsEntry = "";

  let formData = null;
  let orgData = null;
  let orgContactEmail = "";
  $: isLoadingFormData = true;
  const updateFormQuery = (queryOptions) => {
    client
      .query({
        ...queryOptions,
        query: QUERIES.form,
        variables: { formId: formId },
      })
      .then((result) => {
        formData = _get(result, "data.hostedForm.form") || null;
        orgData = _get(result, "data.hostedForm.org") || null;
        orgContactEmail = _get(result, "data.hostedForm.contactEmail") || "";
        isLoadingFormData = false;
      });
  };
  $: formId && updateFormQuery();
  let orgLogoUrl;
  $: orgLogoUrl = _get(orgData, "logoHandle")
    ? getFilestackUrl(_get(orgData, "logoHandle"), "HOSTED_FORM_HEADER")
    : null;

  let orgName;
  $: orgName = _get(orgData, "name");

  $: isDeleted = _get(formData, "isDeleted", false);

  // ATTENTION: This is an important API for the EditFrom page preview of the hosted form.
  window.refreshHostedFormData = () =>
    updateFormQuery({ fetchPolicy: "no-cache" });
  $: pageTitle = `${_get(formData, "title") || "Hosted Form"} • ${
    _get(orgData, "name") || "Loading..."
  }`;

  $: formFields = _get(formData, "spec.fields") || [];
  $: firstNameField = _find(formFields, {
    contactField: { isFirstName: true },
  });
  $: lastNameField = _find(formFields, {
    contactField: { isLastName: true },
  });
  $: emailField = _find(formFields, {
    contactField: { isEmail: true },
  });
  $: smsField = _find(formFields, {
    contactField: { isSms: true },
  });

  $: formEndpoint = _get(formData, "_id")
    ? getFormEndpoint(_get(formData, "_id"))
    : null;
  let isSaving = false;
  let isComplete = false;

  function phoneNumberValidator(number) {
    // Remove all non-digit characters using a regular expression.
    const cleaned = number.replace(/[^0-9]/g, "");

    // Check if the cleaned number contains 10 or 11 digits.
    const length = cleaned.length;

    if (length === 10) {
      // 10 digits is valid.
      return true;
    } else if (length === 11) {
      // If it's 11 digits, the first digit must be '1'.
      return cleaned[0] === "1";
    }

    // If it's neither 10 nor 11 digits, it's invalid.
    return false;
  }
  async function onSubmit() {
    if (isSaving) return;
    if (!emailEntry && !smsEntry) {
      alert("Email or phone number is required.");
      return;
    }

    if (smsEntry && !phoneNumberValidator(smsEntry)) {
      alert("Please input vaild phone number.");
      return;
    }

    isSaving = true;
    try {
      // grecaptcha.enterprise.ready(function() { grecaptcha.enterprise.execute(...) });
      let recaptchaToken = await window.grecaptcha.enterprise.execute(
        __GCP_RECAPTCHA_KEY_SCORE__,
        { action: "SUBMIT_HOSTED_FORM" }
      );
      let formBody = {
        recaptchaToken: recaptchaToken,
        [firstNameField.apiName]: firstNameEntry,
        [lastNameField.apiName]: lastNameEntry,
        [emailField.apiName]: emailEntry,
        [smsField.apiName]: smsEntry,
      };
      let response = await fetch(formEndpoint, {
        method: "POST",
        mode: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formBody),
      });
      if (response.ok) {
        isComplete = true;
      } else {
        let serverMessage = await response.text();
        const payload = {
          url: window.location.href,
          data: formBody,
        };
        const backupMessage = `Could not save info. It's not your fault, but we need your help. Please email this info to ${__APP_HELP_EMAIL__} and we'll sort everything out: "${JSON.stringify(
          payload
        )}". Thank you!`;
        alert(serverMessage || backupMessage);
      }
    } catch (error) {
      console.error(error);
      alert("Could not connect. Please try again.");
    }
    isSaving = false;
  }
  function doConfetti() {
    party.screen({
      color: ["#4286F4"],
      size: party.minmax(6, 12),
      count: party.variation(300 * (window.innerWidth / 1980), 0.4),
      angle: -180,
      spread: 80,
      angularVelocity: party.minmax(6, 9),
    });
  }
  $: if (isComplete) {
    doConfetti();
  }

  const hasOpener = !!window.opener;
  const isFramed = window.parent !== window;
  const hasReferrer = document.referrer != window.location.href;
  // let isFormPreview = false;
  // if (isFramed) {
  //     try {
  //         // This throws an error if the frame isn't on the same origin as the parent.
  //         window.parent.name;
  //         isFormPreview = true;
  //     } catch {}
  // }
  const isClosable = hasOpener || isFramed || hasReferrer;
  // console.log(hasOpener, isFramed, hasReferrer, isFormPreview, isClosable);
  let isClosing = false;
  function requestCloseWindow() {
    isClosing = true;
    if (hasOpener) {
      // window.close();
      window.opener.postMessage("REQUEST_CLOSE", "*");
    } else if (isFramed) {
      window.parent.postMessage("REQUEST_CLOSE", "*");
    } else if (hasReferrer) {
      window.location = document.referrer;
    }
  }

  let selectedTag = "";
  let isModalOpening = false;

  function handleModalClose() {
    isModalOpening = false;
    selectedTag = "";
  }
</script>

<HtmlHeadTitle {pageTitle} hideBranding />
{#if isModalOpening}
  <PrivacyAndTermsPopUp
    {handleModalClose}
    {selectedTag}
    {orgLogoUrl}
    {orgName}
    {orgContactEmail}
  />
{:else}<main class:loading={isLoadingFormData}>
    {#if isLoadingFormData}
      <div class="loading-container">
        <div class="spinner">
          <div class="double-bounce1"></div>
          <div class="double-bounce2"></div>
        </div>
      </div>
    {:else if !isClosing}
      {#if isDeleted}
        <div
          class="disabled-form-card"
          transition:fly={{ y: 30, duration: 500, easing: quintOut }}
        >
          {#if isClosable}
            <nav class="window-controls cols">
              <div></div>
              <div>
                <button
                  type="button"
                  class="button clear button-round"
                  on:click={requestCloseWindow}
                >
                  <ion-icon name="close" class="btn-icon"></ion-icon>
                </button>
              </div>
            </nav>
          {/if}
          <div class="disabled-alert">
            <ion-icon name="warning-outline" class="status-icon" />
          </div>
          <div class="disabled-text">This form is disabled.</div>
        </div>
      {:else}
        <div
          class="form-card"
          transition:fly={{ y: 30, duration: 500, easing: quintOut }}
        >
          {#if isClosable}
            <nav class="window-controls cols">
              <div>
                <!-- needed for empty column to push X col to right (due to space-between) -->

                {#if isSecure}
                  <div class="badge-secured">
                    <ion-icon
                      name="lock-closed"
                      style="font-size:16px; position:relative; top:1px; --ionicon-stroke-width:48px;"
                    ></ion-icon>
                    Secured
                  </div>
                {/if}

                <!-- {:else}
                            <div class="badge-secured">
                                <ion-icon name="lock-open"></ion-icon>
                                Not Secured
                            </div> -->
              </div>
              <div>
                <button
                  type="button"
                  class="button clear button-round"
                  on:click={requestCloseWindow}
                >
                  <ion-icon name="close" class="btn-icon"></ion-icon>
                </button>
              </div>
            </nav>
          {/if}
          <header>
            {#if orgLogoUrl}
              <div
                style="padding: 0 20px; /* To prevent the logo from being too wide */"
              >
                <address
                  class="org-logo"
                  style="background-image:url({orgLogoUrl});"
                ></address>
              </div>
              <!-- {:else}
                    <h1>
                        {_get(orgData, 'name')}
                    </h1> -->
            {:else if _get(orgData, "name")}
              <address class="org-name">
                {_get(orgData, "name")}
              </address>
            {/if}
            <div class="form-heading">
              <h1 class="form-title">
                {_get(formData, "name")}
              </h1>
              {#if _get(formData, "willSendMessage")}
                <h2 class="form-subtitle">
                  Sends instantly via email or text message
                </h2>
              {/if}
            </div>
          </header>
          {#if !isComplete}
            <form
              on:submit|preventDefault={onSubmit}
              disabled={isSaving}
              class:saving={isSaving}
              transition:fade|local={{ duration: 300 }}
              on:outroend={() => {
                formOutro = true;
              }}
            >
              <div class="form-body">
                <div class="fieldrow">
                  <label for="fame" class="fieldlabel"> First Name </label>
                  <input
                    bind:value={firstNameEntry}
                    disabled={isSaving}
                    placeholder="Fran"
                    type="text"
                    id="fame"
                    autofocus
                  />
                </div>
                <div class="fieldrow">
                  <label for="lame" class="fieldlabel"> Last Name </label>
                  <input
                    bind:value={lastNameEntry}
                    disabled={isSaving}
                    placeholder="Smith"
                    type="text"
                    id="lame"
                  />
                </div>
                <div class="fieldrow">
                  <label for="proton" class="fieldlabel"> Email Address </label>
                  <!-- <div class="fieldhelp">
                                We'll email you at this address.
                            </div> -->
                  <input
                    bind:value={emailEntry}
                    disabled={isSaving}
                    placeholder="fran@protonmail.com"
                    type="email"
                    id="proton"
                  />
                </div>
                <div class="fieldrow">
                  <label for="att" class="fieldlabel">
                    Phone Number <span style="font-weight:400;"
                      >for text messages</span
                    >
                  </label>
                  <!-- <div class="fieldhelp">
                                We'll text you at this number.
                            </div> -->
                  <input
                    bind:value={smsEntry}
                    disabled={isSaving}
                    placeholder="555 000 0000"
                    type="tel"
                    id="att"
                  />
                </div>
                <div class="form-optin-checkbox">
                  <div>
                    <input type="checkbox" required disabled={isSaving} />
                  </div>
                  <div class="form-optin-checkbox-span">
                    <span
                      >By entering your number, you consent to receiving SMS
                      messages from <strong>{_get(orgData, "name")}</strong>
                      To opt out at any time, simply reply to any message from us.
                      Msg and data rates may apply. Message frequency may vary.
                    </span>
                  </div>
                </div>
              </div>
              <div class="form-controls">
                <!--
                        <button type="button" disabled={isSaving} class="secondary" class:muted={isSaving}>
                            <ion-icon name="arrow-back" class="btn-icon" />
                            <span class="btn-text">
                                Cancel
                            </span>
                        </button>
                        -->
                <button
                  type="submit"
                  disabled={isSaving}
                  class="button clear"
                  class:muted={isSaving}
                >
                  <span class="btn-text"> Submit </span>
                  <ion-icon
                    name="arrow-forward"
                    class="btn-icon"
                    style="margin-right:-18px;"
                  />
                </button>
              </div>
            </form>
          {:else if formOutro}
            <div
              class="form-body"
              style="padding-bottom:30px;"
              transition:fade|local={{ duration: 300 }}
            >
              <div
                style="text-align:center; padding:15px 0; line-height:1.5; font-size:30px;"
              >
                <button
                  type="button"
                  class="button clear"
                  on:click={doConfetti}
                  style="margin:0 auto; font-size:40px; height:50px;"
                >
                  🎉
                </button>
                <div style="font-weight:600; font-size:25px;">Thank you!</div>
              </div>
              {#if emailEntry}
                <div class="fieldrow">
                  <label for="proton" class="fieldlabel">
                    We'll email you at...
                  </label>
                  <input
                    readonly
                    disabled
                    value={emailEntry}
                    type="email"
                    id="proton"
                  />
                </div>
              {/if}
              {#if smsEntry}
                <div class="fieldrow">
                  <label for="att" class="fieldlabel">
                    We'll text you at...
                  </label>
                  <input
                    readonly
                    disabled
                    value={smsEntry}
                    type="tel"
                    id="att"
                  />
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/if}
    {/if}
    <footer>
      <div class="cols">
        <div style="flex-basis:41%; flex-grow:1; text-align:center;">
          <!-- <a
                    target="_blank"
                    href="mailto:{__APP_HELP_EMAIL__}"
                    style="font-weight:700;"
                >
                    Need Help?
                </a> -->
          <!-- <a target="_blank" href="{__APP_MARKETING_HOMEPAGE__}/privacy"> -->
          <span
            class="footer-link"
            on:click|stopPropagation={() => {
              isModalOpening = true;
              selectedTag = "Privacy Policy";
            }}>Privacy Policy</span
          >
          <!-- </a> -->
        </div>
        <div
          style="flex-basis:18%; flex-grow:0; flex-shrink:0; text-align:center;"
        >
          <a
            target="_blank"
            href={__APP_MARKETING_HOMEPAGE__}
            style="margin-top:1px /* for vertical alignment */;"
            ><Tooltip content="Powered By LeadAction">
              <span class="app-logomark-white desktop-inline-block"></span>
              <span class="app-logomark-dark mobile-inline-block"
              ></span></Tooltip
            >
          </a>
        </div>
        <div style="flex-basis:41%; flex-grow:1; text-align:center;">
          <!-- <a
                    target="_blank"
                    href="{__APP_MARKETING_HOMEPAGE__}/terms"
                > -->
          <span
            class="footer-link"
            on:click|stopPropagation={() => {
              isModalOpening = true;
              selectedTag = "Terms of Service";
            }}>Terms of Service</span
          >
          <!-- </a> -->
        </div>
      </div>
    </footer>
  </main>{/if}

<style>
  .cols {
    display: flex;
    flex-direction: row;
  }
  .mobile-inline-block {
    display: inline-block;
  }
  .desktop-inline-block {
    display: none;
  }
  @media only screen and (min-width: 480px) {
    .mobile-inline-block {
      display: none;
    }
    .desktop-inline-block {
      display: inline-block;
    }
  }
  main {
    margin: 0 auto;
    padding: 0;
    min-height: 100vh;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }
  @media only screen and (min-width: 480px) {
    main {
      max-width: 360px;
      padding-top: 50px;
      padding-bottom: 150px;
    }
  }
  main.loading {
    padding-bottom: 0; /* to position sticky footer */
  }
  .loading-container {
    margin-top: 100px;
    flex-grow: 1;
    text-align: center;
  }
  .form-card {
    flex-grow: 1;
    background: #fff;
    color: var(--slate);
    box-sizing: border-box;
    padding: 0;
    height: 100%;
    border-bottom: 1px solid var(--slate-o10);
    overflow: hidden;
  }
  @media only screen and (min-width: 480px) {
    .form-card {
      flex-grow: 0;
      border-radius: 30px;
      box-shadow: 0 10px 30px 0 rgba(0, 0, 0, 0.2);
      border: none;
    }
    .disabled-form-card {
      border-radius: 30px;
      box-shadow: 0 10px 30px 0 rgba(0, 0, 0, 0.2);
      border: none;
    }
  }
  .window-controls {
    padding: 10px 10px 0;
    margin-bottom: -20px;
    justify-content: space-between;
    align-items: center;
  }
  .badge-secured {
    font-size: 11px;
    font-weight: 600;
    line-height: 30px;
    color: var(--blue);
  }
  .window-controls .badge-secured {
    margin-left: 10px;
  }
  .badge-deleted {
    font-size: 14px;
    font-weight: 600;
    line-height: 30px;
    color: var(--red);
  }
  .window-controls .button-round {
    width: 40px;
    height: 40px;
    padding-left: 0;
    padding-right: 0;
  }
  .form-card header {
    padding: 50px 20px 10px;
  }
  .org-logo {
    height: 80px;
    margin-bottom: 30px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: auto 100%;
    background-size: contain;
  }
  .org-name {
    margin-bottom: 10px;
    font-size: 24px;
    font-weight: 900;
    /* opacity: 0.6; */
    font-style: normal;
    text-align: center;
  }
  .deleted {
    text-decoration: line-through;
  }
  .form-heading {
    padding: 0;
  }
  .form-title {
    font-size: 16px;
    line-height: 1.2;
    font-weight: 600;
    padding: 0;
    margin: 0;
    opacity: 0.9;
    text-align: center;
  }
  .form-subtitle {
    font-size: 14px;
    line-height: 1.2;
    font-weight: 400;
    padding: 0;
    margin: 6px 0 0;
    text-align: center;
  }
  .form-subtitle-deleted {
    font-size: 14px;
    line-height: 1.2;
    font-weight: 400;
    padding: 0;
    margin: 6px 0 0;
    text-align: center;
    color: var(--red);
  }
  form.saving {
    opacity: 0.5;
  }
  .form-body {
    padding: 0 20px;
  }
  .form-controls {
    display: flex;
    align-items: stretch;
    flex-direction: row;
    padding: 10px 20px 20px;
  }
  .form-controls .button {
    flex-grow: 1;
    flex-shrink: 1;
    padding: 15px 5px;
    height: 54px;
    border-radius: 27px;
  }
  .form-controls .button.clear:hover {
    background: var(--slate-o10);
  }
  .form-optin-checkbox {
    display: flex;
    padding: 5px;
  }
  .form-optin-checkbox-span {
    margin: 0px 4px 0px 4px;
    font-size: 12px;
  }
  .btn-text {
    line-height: 1.15;
    font-size: 18px;
    font-weight: 600;
    padding: 0 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .btn-icon {
    font-size: 24px;
    margin: 0 0px;
    flex-shrink: 0;
    flex-grow: 0;
  }
  footer {
    background: white;
    padding: 12px 20px;
    user-select: none;
  }
  footer .cols {
    align-items: center;
    justify-content: space-between;
  }
  .footer-link {
    display: inline-block;
    color: var(--slate);
    font-size: 12px;
    font-weight: 700;
    opacity: 0.4;
    text-decoration: none;
    padding: 10px 7px;
  }
  @media only screen and (min-width: 480px) {
    footer {
      background: transparent;
    }
    .footer-link {
      color: white;
      opacity: 0.5;
    }
  }
  .footer-link:hover {
    opacity: 1;
  }
  .app-logomark-white,
  .app-logomark-dark {
    height: 29px;
    width: 25px;
  }

  .spinner {
    width: 40px;
    height: 40px;
    position: relative;
    margin: 0 auto;
  }
  .double-bounce1,
  .double-bounce2 {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: var(--slate);
    opacity: 0.4;
    position: absolute;
    top: 0;
    left: 0;
    animation: sk-bounce 2s infinite ease-in-out;
  }
  @media only screen and (min-width: 480px) {
    .double-bounce1,
    .double-bounce2 {
      background-color: #fff;
      opacity: 0.6;
    }
  }
  .double-bounce2 {
    animation-delay: -1s;
  }
  @keyframes sk-bounce {
    0%,
    100% {
      transform: scale(0);
    }
    50% {
      transform: scale(1);
    }
  }
  .status-icon {
    width: 50px;
    height: 50px;
  }
  .disabled-form-card {
    flex-grow: 1;
    background: #fff;
    color: var(--slate);
    box-sizing: border-box;
    padding: 0;
    height: 100%;
    border-bottom: 1px solid var(--slate-o10);
    overflow: hidden;
  }
  .disabled-alert {
    display: flex;
    color: red;
    font-weight: 600;
    justify-content: center;
    padding: 60px 10px 10px 10px;
  }
  .disabled-text {
    display: flex;
    color: red;
    font-weight: 600;
    justify-content: center;
    padding: 20px 10px;
  }
</style>
